import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

/**
 * Custom hook to manage multi-step form logic including state, validation,
 * navigation between steps, data fetching, and submission.
 *
 * @param {Array} steps - Array of step configuration objects for the form.
 * @param {Function} onFinish - Callback to call with final answers when form completes.
 * @param {Function} fetchLeads - Callback to log data before redirecting on error.
 * @param {Function} navigate - Navigation function.
 * @returns {Object} Form state and handlers for use in components.
 */
export function useMultiStepForm(steps, onFinish, fetchLeads, navigate) {
  const hasFetched = useRef(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [stepIndex, setStepIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState("");
  const [textGridValues, setTextGridValues] = useState({});
  const [answers, setAnswers] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [minAmount, setMinAmount] = useState(4000);
  const [validationErrors, setValidationErrors] = useState({});

  const currentStep = steps[stepIndex];

  const resetForm = useCallback(() => {
    setStepIndex(0);
    setSelectedOption("");
    setTextGridValues({});
    setAnswers([]);
    setValidationErrors({});
  }, []);

  const fetchCreditors = useCallback(async () => {
    const response = await fetch(`${apiBaseUrl}/v1/api/lead/creditors`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorBody.message}`
      );
    }

    const data = await response.json();
    return data;
  }, [apiBaseUrl]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadCreditors = async () => {
      try {
        const data = await fetchCreditors();
        setCreditors(data.creditors);
        setMinAmount(data.minFrom);
      } catch (err) {
        console.error("Failed to fetch creditors:", err);
        toast.error("Could not load creditors list.");
      }
    };

    loadCreditors();
  }, [fetchCreditors]);

  const isValidValue = useCallback((field, value) => {
    if (typeof field === "object") {
      if (field.required && (!value || !value.trim())) {
        return `${field.label || "This"} field is required`;
      }
      if (field.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address";
        }
      }
      if (typeof field.validate === "function") {
        const result = field.validate(value);
        return result === true ? true : result;
      }
    }
    return true;
  }, []);

  const currentValidationErrors = useMemo(() => {
    const errors = {};

    if (currentStep.type === "text-grid" || currentStep.type === "text-area") {
      currentStep.fields.forEach((field) => {
        const label = typeof field === "string" ? field : field.label;
        const value = textGridValues[label];
        const validation = isValidValue(field, value);
        if (validation !== true) errors[label] = validation;
      });
    } else if (currentStep.type === "radio" && !selectedOption) {
      errors.general = "Please select an option";
    }

    return errors;
  }, [currentStep, textGridValues, selectedOption, isValidValue]);

  useEffect(() => {
    setValidationErrors(currentValidationErrors);
  }, [currentValidationErrors]);

  const isNextDisabled = Object.keys(currentValidationErrors).length > 0;

  const handleBack = () => {
    if (stepIndex === 0) return;

    const prevStep = steps[stepIndex - 1];
    setStepIndex((prev) => prev - 1);

    if (prevStep.type === "text-grid" || prevStep.type === "text-area") {
      setTextGridValues(answers[prevStep.key] || {});
      setSelectedOption("");
    } else {
      setSelectedOption(answers[prevStep.key] || "");
      setTextGridValues({});
    }

    setValidationErrors({});
  };

  useEffect(() => {
    const step = steps[stepIndex];
    const savedAnswer = answers[step.key];

    if (step.type === "radio") {
      setSelectedOption(savedAnswer || "");
      setTextGridValues({});
    } else if (step.type === "text-grid" || step.type === "text-area") {
      setTextGridValues(savedAnswer || {});
      setSelectedOption("");
    } else {
      setSelectedOption(savedAnswer || "");
      setTextGridValues({});
    }
  }, [stepIndex, steps, answers]); // ✅ Fixed dependency array

  const handleBusinessRules = (stepKey, updatedAnswers) => {
    const values = updatedAnswers[stepKey] || {};
    const state = values["State the lawsuit was filed in"];

    if (state !== undefined && state !== "California") {
      toast.error("We currently only handle lawsuits filed in California.", {
        position: "bottom-center",
        autoClose: 6000,
      });
      return false;
    }

    return true;
  };

  const handleRules = async (updatedAnswers) => {
    console.log("🟡 handleRules called with:", updatedAnswers);

    const amountStr = updatedAnswers.debtAmount?.Amount;
    const amount = parseFloat(amountStr);
    const debtCollectorAction = updatedAnswers.debtCollectorAction;

    try {
      console.log("🔍 Parsed amount:", amount);
      console.log("🔍 Debt collector action:", debtCollectorAction);

      if (
        debtCollectorAction &&
        debtCollectorAction.toLowerCase() !==
          "sent me a new lawsuit (summons)".toLowerCase()
      ) {
        console.log("❌ Debt collector action is not valid. Fetching leads...");
        await fetchLeads(updatedAnswers, true);
        console.log("➡️ Navigating to /error due to invalid action");
        navigate("/error");
        return false;
      }

      if (isNaN(amount) || amount < minAmount) {
        console.log("❌ Amount is invalid or below minimum. Fetching leads...");
        await fetchLeads(updatedAnswers, true);
        console.log("➡️ Navigating to /error due to invalid amount");
        navigate("/error");
        return false;
      }

      const creditor = updatedAnswers.lawsuitDetails?.["creditor (plaintiff)"];
      console.log("🔍 Creditor:", creditor);

      if (creditor && !creditors.includes(creditor.trim())) {
        console.log("❌ Creditor not in approved list. Fetching leads...");
        await fetchLeads(updatedAnswers, true);
        console.log("➡️ Navigating to /error due to invalid creditor");
        navigate("/error");
        return false;
      }

      console.log("✅ All checks passed");
      return true;
    } catch (e) {
      console.error("🔥 Exception during handleRules:", e);
      console.log("➡️ Navigating to /error due to exception");
      navigate("/error");
      return false;
    }
  };

  const handleNext = async () => {
    if (isNextDisabled) return;

    const newAnswer =
      currentStep.type === "text-grid" || currentStep.type === "text-area"
        ? textGridValues
        : selectedOption;

    const updatedAnswers = { ...answers, [currentStep.key]: newAnswer };
    setAnswers(updatedAnswers);

    if (currentStep.key === "debtAmount") {
      const enteredAmount = parseFloat(updatedAnswers.debtAmount?.Amount);
      if (!isNaN(enteredAmount) && enteredAmount < 7000) {
        navigate("/error", { state: { message: "Sorry, we cannot take cases under $7000" } });
        return;
      }
    }

    if (!handleBusinessRules(currentStep.key, updatedAnswers)) return;

    // ✅ Wait for async rule check before proceeding
    if (stepIndex === steps.length - 2) {
      const passed = await handleRules(updatedAnswers);
      if (!passed) return;
    }

    const nextStepIndex = stepIndex + 1;
    setStepIndex(nextStepIndex);
  };

  return {
    stepIndex,
    currentStep,
    selectedOption,
    setSelectedOption,
    textGridValues,
    setTextGridValues,
    handleBack,
    handleNext,
    isNextDisabled,
    validationErrors,
    stepsLength: steps.length,
    answers,
    creditors,
    resetForm,
  };
}
